import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetIdentity } from "@refinedev/core";
import {
  PermissionGuard,
  NoPermissionFallback,
} from "../../components/PermissionGuard";
import { User } from "../../interfaces/user";

interface EditUserData {
  name: string;
  email: string;
  password: string;
  role: string;
  isActive: boolean;
}

const AdminEditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: currentUser } = useGetIdentity<User>();
  const [user, setUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<EditUserData>({
    name: "",
    email: "",
    password: "",
    role: "user",
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // 载入用户数据
  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("未找到授權令牌");
        }

        const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
        const response = await fetch(`${API_URL}/auth/users/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("載入用戶資料失敗");
        }

        const data = await response.json();
        const userData = data.data.user;
        setUser(userData);
        setFormData({
          name: userData.name || "",
          email: userData.email || "",
          password: "", // 密码字段留空
          role: userData.role || "user",
          isActive: userData.isActive !== false,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "載入失敗");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  // 处理表单变更
  const handleInputChange = (field: keyof EditUserData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // 清除相关错误
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // 验证密码强度
  const validatePassword = (password: string): string[] => {
    if (!password) return []; // 密码为空时不验证

    const errors: string[] = [];
    if (password.length < 8) {
      errors.push("密碼長度至少8個字元");
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push("密碼必須包含至少一個小寫字母");
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push("密碼必須包含至少一個大寫字母");
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push("密碼必須包含至少一個數字");
    }
    return errors;
  };

  // 验证邮箱格式
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证表单
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = '請輸入姓名';
    }

    if (!formData.email.trim()) {
      errors.email = '請輸入電子郵件';
    } else if (!validateEmail(formData.email)) {
      errors.email = '請輸入有效的電子郵件地址';
    }

    if (formData.password) {
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        errors.password = passwordErrors.join(', ');
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("未找到授權令牌");
      }

      // 构建请求数据，只包含有变更的字段
      const updateData: any = {};
      if (formData.name !== user?.name) updateData.name = formData.name;
      if (formData.email !== user?.email) updateData.email = formData.email;
      if (formData.role !== user?.role) updateData.role = formData.role;
      if (formData.isActive !== user?.isActive) updateData.isActive = formData.isActive;
      if (formData.password) updateData.password = formData.password;

      if (Object.keys(updateData).length === 0) {
        setError("沒有需要更新的資料");
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
      const response = await fetch(`${API_URL}/auth/users/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccess("用戶資料更新成功！");
        setUser(data.data.user);
        // 如果修改了密码，清空密码字段
        if (formData.password) {
          setFormData(prev => ({ ...prev, password: "" }));
        }

        // 3秒后跳转回详情页面
        setTimeout(() => {
          navigate(`/users/${id}`);
        }, 2000);
      } else {
        throw new Error(data.message || "更新失敗");
      }
    } catch (err) {
      console.error("更新用戶資料錯誤:", err);
      setError(err instanceof Error ? err.message : "更新失敗");
    } finally {
      setUpdating(false);
    }
  };

  // 格式化角色显示
  const formatRole = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: "管理員",
      maintainer: "維護人員",
      user: "一般用戶",
    };
    return roleMap[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // 只有admin可以使用这个页面
  if (currentUser?.role !== 'admin') {
    return <NoPermissionFallback />;
  }

  // 不能编辑其他admin用户
  if (user?.role === 'admin' && currentUser?.id !== user?.id) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">權限不足</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>無法編輯其他管理員用戶的資料。</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      requiredRoles={["admin"]}
      fallback={<NoPermissionFallback />}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        {/* 面包屑导航 */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <div>
                <Link to="/users" className="text-gray-400 hover:text-gray-500">
                  <svg className="flex-shrink-0 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="sr-only">返回</span>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <Link to="/users" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                  用戶管理
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <Link to={`/users/${id}`} className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                  {user?.name}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="ml-4 text-sm font-medium text-gray-500">編輯用戶</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* 页面标题 */}
        <div className="mt-6">
          <h1 className="text-2xl font-bold text-gray-900">編輯用戶資料</h1>
          <p className="mt-2 text-sm text-gray-600">
            修改用戶 "{user?.name}" 的基本資料、角色和權限設定
          </p>
        </div>

        {/* 成功和错误消息 */}
        {success && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{success}</p>
                <p className="text-sm text-green-700">即將跳轉到用戶詳情頁面...</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* 编辑表单 */}
        <div className="mt-8 max-w-2xl">
          <div className="bg-white shadow rounded-lg">
            <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
              <div className="space-y-6">
                {/* 姓名 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    姓名 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      formErrors.name
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="請輸入用戶姓名"
                  />
                  {formErrors.name && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>

                {/* 电子邮件 */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    電子郵件 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      formErrors.email
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="請輸入電子郵件地址"
                  />
                  {formErrors.email && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>

                {/* 新密码 */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    新密碼
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      formErrors.password
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="如需修改密碼請輸入新密碼"
                  />
                  {formErrors.password && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.password}</p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    留空表示不修改密碼。新密碼必須至少8個字元，包含大小寫字母和數字
                  </p>
                </div>

                {/* 角色 */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    用戶角色 *
                  </label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="user">一般用戶</option>
                    <option value="maintainer">維護人員</option>
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    選擇用戶的系統角色和權限等級
                  </p>
                </div>

                {/* 帐号状态 */}
                <div>
                  <div className="flex items-center">
                    <input
                      id="isActive"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      啟用帳號
                    </label>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    取消勾選將停用此帳號，用戶將無法登入系統
                  </p>
                </div>

                {/* 当前信息显示 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">當前用戶資訊</h3>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <dt className="font-medium text-gray-500">建立時間：</dt>
                      <dd className="text-gray-900">{user?.createdAt ? new Date(user.createdAt).toLocaleDateString("zh-TW") : '-'}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">最後登入：</dt>
                      <dd className="text-gray-900">{user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString("zh-TW") : '從未登入'}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">API Key：</dt>
                      <dd className="text-gray-900 font-mono text-xs">{user?.apikey || '未設定'}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">用戶ID：</dt>
                      <dd className="text-gray-900 font-mono text-xs">{user?.id}</dd>
                    </div>
                  </dl>
                </div>

                {/* 提交按钮 */}
                <div className="flex justify-end space-x-3">
                  <Link
                    to={`/users/${id}`}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    取消
                  </Link>
                  <button
                    type="submit"
                    disabled={updating}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        更新中...
                      </>
                    ) : (
                      "更新用戶資料"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default AdminEditUserPage;